import { Injectable, inject, signal } from '@angular/core';

import { merge, Observable, Subject } from 'rxjs';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { LanguageService } from '@fcom/ui-translate';

import { BreadcrumbItem, HeroBackground } from '../../interfaces/menu.interface';
import { SchemaService } from '../schema/schema.service';

@Injectable()
export class BreadcrumbService {
  public breadcrumb$: Observable<BreadcrumbItem[]>;
  private breadcrumbItemsNormal$: Subject<BreadcrumbItem[]> = new Subject<BreadcrumbItem[]>(); // Populated from navigationPathList in CMS payload
  private breadcrumbItemsOverride$: Subject<BreadcrumbItem[]> = new Subject<BreadcrumbItem[]>(); // Populated by setBreadcrumb

  private readonly schemaService = inject(SchemaService);
  private readonly configService = inject(ConfigService);
  private readonly languageService = inject(LanguageService);

  private readonly homeLabel = this.languageService.translateSignal('actions.home');

  /** What the current page paints directly behind the breadcrumb strip: a light or dark hero image
   * (e.g. a tier gradient) that the strip's text colour has to account for, or nothing. The page
   * that owns the hero sets it (see LoyaltyViewComponent); the breadcrumb itself stays page-agnostic
   * and only reads it. */
  private readonly _heroBackground = signal(HeroBackground.NONE);
  readonly heroBackground = this._heroBackground.asReadonly();
  /** Which caller last set `_heroBackground` (e.g. a specific LoyaltyViewComponent instance).
   * Lets `clearHeroBackground` tell "my value is still live" apart from "a newer instance
   * already set the same value" without relying on a value comparison that can't distinguish
   * the two (same-tier navigations set the same HeroBackground value). */
  private heroBackgroundOwner?: unknown;

  constructor() {
    this.constructBreadcrumbItemsNormal$();
    this.constructBreadcrumbItemsOverride$();
    this.constructBreadcrumbItems$();
  }

  public setBreadcrumbNormal(breadcrumbItems: BreadcrumbItem[]): void {
    this.breadcrumbItemsNormal$.next(breadcrumbItems);
    this.updateBreadcrumbSchema(breadcrumbItems);
  }

  public setBreadcrumb(breadcrumbItems: BreadcrumbItem[]): void {
    this.breadcrumbItemsOverride$.next(breadcrumbItems);
    this.updateBreadcrumbSchema(breadcrumbItems);
  }

  public setHeroBackground(heroBackground: HeroBackground, owner: unknown): void {
    this.heroBackgroundOwner = owner;
    this._heroBackground.set(heroBackground);
  }

  /** Only clears when `owner` is still the one that last set the background — otherwise a newer
   * owner (e.g. the next loyalty view instance) has already taken over and this call is a stale
   * destroy racing behind it, so it must be a no-op. */
  public clearHeroBackground(owner: unknown): void {
    if (this.heroBackgroundOwner === owner) {
      this.heroBackgroundOwner = undefined;
      this._heroBackground.set(HeroBackground.NONE);
    }
  }

  private updateBreadcrumbSchema(items: BreadcrumbItem[]): void {
    const baseUrl = this.configService.cfg.baseUrl;
    const languageRootUrl = `/${this.languageService.langValue}`;
    const homeItem: BreadcrumbItem = {
      title: this.homeLabel() ?? 'Home',
      url: languageRootUrl,
    };
    const schemaItems = items.filter(({ url }) => url !== languageRootUrl);

    this.schemaService.addBreadcrumbListSchema({ items: [homeItem, ...schemaItems], baseUrl });
  }

  private constructBreadcrumbItems$() {
    this.breadcrumb$ = merge(this.breadcrumbItemsNormal$, this.breadcrumbItemsOverride$);
  }

  private constructBreadcrumbItemsNormal$() {
    this.breadcrumbItemsNormal$ = new Subject<BreadcrumbItem[]>();
  }

  private constructBreadcrumbItemsOverride$() {
    this.breadcrumbItemsOverride$ = new Subject<BreadcrumbItem[]>();
  }
}
